import React, { useState, useEffect } from 'react';
import {
  Card, Input, Button, Select, Tag, Space, Tabs, List, Typography,
  Modal, Form, message, Row, Col, Divider, Empty, Tooltip,
} from 'antd';
import {
  TranslationOutlined, CopyOutlined, CheckOutlined, PlusOutlined, BookOutlined,
  HistoryOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import { translationApi } from '../services/api';
import { fmtCN } from '../utils/time';

const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;

const toneOptions = [
  { value: 'neutral', label: '中性语气', color: 'blue' },
  { value: 'tough', label: '强硬语气', color: 'orange' },
  { value: 'angry', label: '生气语气', color: 'red' },
  { value: 'impatient', label: '不耐烦语气', color: 'purple' },
];

const categoryOptions = [
  { value: 'general', label: '通用' },
  { value: 'carrier', label: '承运商' },
  { value: 'cargo', label: '货物' },
  { value: 'document', label: '单证' },
  { value: 'fee', label: '费用' },
  { value: 'operation', label: '操作' },
  { value: 'exception', label: '异常' },
];

export default function Translation() {
  const [activeTab, setActiveTab] = useState('translate');

  // 翻译
  const [sourceText, setSourceText] = useState('');
  const [targetLang, setTargetLang] = useState('en');
  const [tone, setTone] = useState('neutral');
  const [translating, setTranslating] = useState(false);
  const [translateResult, setTranslateResult] = useState(null);
  const [copyCopied, setCopyCopied] = useState(false);

  // 术语库
  const [terms, setTerms] = useState([]);
  const [termsLoading, setTermsLoading] = useState(false);
  const [termKeyword, setTermKeyword] = useState('');
  const [termCategory, setTermCategory] = useState('');
  const [termStatusFilter, setTermStatusFilter] = useState('');
  const [termModalOpen, setTermModalOpen] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);
  const [termForm] = Form.useForm();

  // 历史
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchTerms();
    fetchHistory();
  }, []);

  const fetchTerms = async (statusOverride) => {
    setTermsLoading(true);
    try {
      const status = statusOverride === undefined ? termStatusFilter : statusOverride;
      const params = { page_size: 100 };
      if (termKeyword) params.keyword = termKeyword;
      if (termCategory) params.category = termCategory;
      if (status) params.review_status = status;
      const data = await translationApi.listTerminology(params);
      setTerms(data);
    } catch (e) {} finally {
      setTermsLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const data = await translationApi.getHistory({ page_size: 20 });
      setHistory(data);
    } catch (e) {}
  };

  const handleTranslate = async () => {
    if (!sourceText.trim()) {
      message.warning('请输入要翻译的内容');
      return;
    }
    setTranslating(true);
    setTranslateResult(null);
    try {
      const data = await translationApi.translate({
        text: sourceText,
        target_lang: targetLang,
        tone,
      });
      setTranslateResult(data);
    } catch (e) {} finally {
      setTranslating(false);
    }
  };

  const handleCopyResult = () => {
    if (!translateResult) return;
    const text = translateResult.translated_text || '';
    const done = () => {
      if (copyCopied) return;
      setCopyCopied(true);
      message.success('已复制翻译结果');
      setTimeout(() => setCopyCopied(false), 2000);
    };
    const fail = () => {
      message.error('复制失败，请手动选中文本复制');
    };
    // 主路径：Clipboard API
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(done).catch(() => fallbackCopy(text, done, fail));
    } else {
      fallbackCopy(text, done, fail);
    }
  };

  // 复制降级：隐藏 textarea + execCommand
  const fallbackCopy = (text, done, fail) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.style.top = '0';
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, text.length);
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      ok ? done() : fail();
    } catch (e) {
      fail();
    }
  };

  // 译文中的专业术语译文加粗：返回 React nodes
  // 按 used_terms 的 translation（或 term_en / term_cn）作为关键词，更长的先匹配（避免子串抢占）
  const renderBoldTranslatedText = (result) => {
    const text = result.translated_text;
    if (text == null) return null;
    const raw = String(text);
    const terms = Array.isArray(result.used_terms) ? result.used_terms : [];

    // 提取要加粗的关键词（翻译目标语言的术语译文），去空、去重，长优先
    const target = result.target_lang || targetLang;
    const kws = new Set();
    for (const t of terms) {
      let kw = '';
      if (t && t.translation) kw = String(t.translation);
      if (!kw) kw = target === 'en' ? (t && t.term_en ? String(t.term_en) : '') : (t && t.term_cn ? String(t.term_cn) : '');
      kw = kw.trim();
      if (kw && kw.length >= 1) kws.add(kw);
    }
    const keywords = Array.from(kws).sort((a, b) => b.length - a.length);

    if (!keywords.length) return raw;

    // 正则转义
    const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // 对英文关键词整词匹配（\b）；中文不需要。简单做法：按 target 选择模式
    const pattern = keywords.map((k) => {
      const escaped = escape(k);
      if (/^[A-Za-z0-9'-]+$/.test(k) && target === 'en') {
        return `\\b${escaped}\\b`;
      }
      return escaped;
    }).join('|');
    const re = new RegExp(`(${pattern})`, 'gi');

    const parts = raw.split(re);
    const lowerSet = new Set(keywords.map((k) => k.toLowerCase()));
    return parts.map((seg, i) => {
      if (!seg) return null;
      if (lowerSet.has(seg.toLowerCase())) {
        return <Text key={i} strong style={{ color: '#3E6AE1', fontWeight: 600 }}>{seg}</Text>;
      }
      return seg;
    });
  };

  const handleSaveTerm = async (values) => {
    try {
      let resp;
      if (editingTerm) {
        resp = await translationApi.updateTerminology(editingTerm.id, values);
      } else {
        resp = await translationApi.createTerminology(values);
      }
      message.success(resp?.review_status === 'approved' ? '术语已保存并收录' : '已提交，等待管理员审核');
      setTermModalOpen(false);
      setEditingTerm(null);
      termForm.resetFields();
      fetchTerms();
    } catch (e) {}
  };

  const handleDeleteTerm = async (id) => {
    try {
      await translationApi.deleteTerminology(id);
      message.success('已删除');
      fetchTerms();
    } catch (e) {}
  };

  const openEditTerm = (term) => {
    setEditingTerm(term);
    termForm.setFieldsValue(term);
    setTermModalOpen(true);
  };

  const translateTab = (
    <div>
      <Card className="card-shadow" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <ThunderboltOutlined style={{ fontSize: 20, color: '#3E6AE1' }} />
          <Text strong style={{ fontSize: 16, fontWeight: 500 }}>专业翻译</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>自动调取行业术语库，支持多种语气，拟人化翻译</Text>
        </div>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>原文</Text>
              <Select
                value={targetLang}
                onChange={setTargetLang}
                style={{ width: 120 }}
                options={[
                  { value: 'en', label: '中 → 英' },
                  { value: 'zh', label: '英 → 中' },
                ]}
              />
            </div>
            <TextArea
              autoSize={{ minRows: 10, maxRows: 40 }}
              placeholder={targetLang === 'en' ? '输入中文内容...' : '输入英文内容...'}
              value={sourceText}
              onChange={(e) => setSourceText(e.target.value)}
            />
          </Col>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>译文</Text>
              {translateResult && (
                <Tooltip title={copyCopied ? '已复制到剪贴板' : '一键复制译文'}>
                  <Button
                    size="small"
                    type={copyCopied ? 'primary' : 'default'}
                    icon={copyCopied ? <CheckOutlined /> : <CopyOutlined />}
                    onClick={handleCopyResult}
                    style={{ borderRadius: 4 }}
                  >
                    {copyCopied ? '已复制' : '复制译文'}
                  </Button>
                </Tooltip>
              )}
            </div>
            <div className="translate-result" style={{
              minHeight: 240,
              padding: 12,
              background: '#fafafa',
              border: '1px solid #f0f0f0',
              borderRadius: 6,
              lineHeight: 1.8,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
              {translating ? (
                <Text type="secondary">翻译中...</Text>
              ) : translateResult ? (
                renderBoldTranslatedText(translateResult)
              ) : (
                <Text type="secondary">翻译结果将显示在这里</Text>
              )}
            </div>
          </Col>
        </Row>

        <Divider style={{ margin: '16px 0' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space wrap>
            <Text type="secondary">语气选择：</Text>
            {toneOptions.map(t => (
              <Tag.CheckableTag
                key={t.value}
                checked={tone === t.value}
                onChange={() => setTone(t.value)}
                style={{ padding: '4px 12px', borderRadius: 4, fontSize: 13 }}
              >
                {t.label}
              </Tag.CheckableTag>
            ))}
          </Space>
          <Button type="primary" size="large" icon={<TranslationOutlined />} loading={translating} onClick={handleTranslate}>
            翻译
          </Button>
        </div>

        {translateResult?.used_terms?.length > 0 && (
          <div style={{ marginTop: 16, padding: 12, background: '#F4F4F4', borderRadius: 12 }}>
            <Text strong style={{ fontSize: 13, color: '#171A20', fontWeight: 500 }}>匹配的专业术语调用结果：</Text>
            <div style={{ marginTop: 8 }}>
              {translateResult.used_terms.map((t, i) => (
                <Tag key={i} color="blue" className="term-tag">
                  {t.term_cn} = {t.term_en}
                </Tag>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );

  const termsTab = (
    <div>
      <Card className="card-shadow" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="搜索术语"
            value={termKeyword}
            onChange={(e) => setTermKeyword(e.target.value)}
            onPressEnter={fetchTerms}
            style={{ width: 240 }}
            allowClear
          />
          <Select
            placeholder="分类"
            value={termCategory || undefined}
            onChange={(v) => { setTermCategory(v); }}
            style={{ width: 140 }}
            allowClear
            options={categoryOptions}
          />
          <Select
            value={termStatusFilter || 'all'}
            onChange={(v) => { const s = v === 'all' ? '' : v; setTermStatusFilter(s); fetchTerms(s); }}
            style={{ width: 130 }}
            options={[
              { value: 'all', label: '全部状态' },
              { value: 'approved', label: '已收录' },
              { value: 'pending_approval', label: '待审核' },
              { value: 'rejected', label: '已驳回' },
            ]}
          />
          <Button type="primary" onClick={fetchTerms}>搜索</Button>
          <Button icon={<PlusOutlined />} onClick={() => { setEditingTerm(null); termForm.resetFields(); setTermModalOpen(true); }}>
            添加术语
          </Button>
        </Space>
      </Card>
      <Card className="card-shadow">
        <List
          loading={termsLoading}
          dataSource={terms}
          locale={{ emptyText: '暂无术语，系统会从邮件中自动提取，也可手动添加' }}
          renderItem={(item) => {
            const statusMap = {
              approved: { color: 'green', text: '已收录' },
              pending_approval: { color: 'orange', text: '待审核' },
              rejected: { color: 'red', text: '已驳回' },
            };
            const st = statusMap[item.review_status];
            return (
            <List.Item
              actions={[
                item.review_status !== 'approved' && (
                  <Button key="edit" type="link" size="small"
                    style={item.review_status === 'rejected' ? { color: '#ff4d4f' } : undefined}
                    onClick={() => openEditTerm(item)}>
                    {item.review_status === 'rejected' ? '修改后重新提交' : '编辑'}
                  </Button>
                ),
              ]}
            >
              <List.Item.Meta
                title={
                  <Space wrap>
                    <Text strong>{item.term_cn}</Text>
                    <Text type="secondary">=</Text>
                    <Text strong style={{ color: '#3E6AE1' }}>{item.term_en}</Text>
                    {st && <Tag color={st.color}>{st.text}</Tag>}
                    <Tag color={item.source === 'auto' ? 'green' : 'blue'}>{item.source === 'auto' ? '自动提取' : '手动录入'}</Tag>
                    <Tag>{categoryOptions.find(c => c.value === item.category)?.label || item.category}</Tag>
                  </Space>
                }
                description={
                  <div>
                    {item.review_status === 'rejected' && item.reject_reason && (
                      <div style={{ color: '#ff4d4f', fontSize: 13, marginBottom: 4 }}>
                        驳回原因：{item.reject_reason}
                      </div>
                    )}
                    {item.context && <div style={{ fontSize: 13 }}>场景：{item.context}</div>}
                    <Text type="secondary" style={{ fontSize: 12 }}>使用 {item.use_count} 次</Text>
                  </div>
                }
              />
            </List.Item>
            );
          }}
        />
      </Card>

      <Modal
        title={editingTerm?.review_status === 'rejected' ? '修改后重新提交' : (editingTerm ? '编辑术语' : '添加术语')}
        open={termModalOpen}
        onCancel={() => { setTermModalOpen(false); setEditingTerm(null); }}
        footer={null}
      >
        <Form form={termForm} layout="vertical" onFinish={handleSaveTerm}>
          <Form.Item name="term_cn" label="中文术语" rules={[{ required: true, message: '请输入中文术语' }]}>
            <Input placeholder="中文术语" />
          </Form.Item>
          <Form.Item name="term_en" label="英文术语" rules={[{ required: true, message: '请输入英文术语' }]}>
            <Input placeholder="英文术语" />
          </Form.Item>
          <Form.Item name="category" label="分类" initialValue="general">
            <Select options={categoryOptions} />
          </Form.Item>
          <Form.Item name="context" label="使用场景/上下文">
            <TextArea rows={3} placeholder="描述这个术语的使用场景" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">保存</Button>
              <Button onClick={() => { setTermModalOpen(false); setEditingTerm(null); }}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );

  const historyTab = (
    <Card className="card-shadow">
      <List
        dataSource={history}
        locale={{ emptyText: '暂无翻译历史' }}
        renderItem={(item) => (
          <List.Item>
            <List.Item.Meta
              title={
                <Space>
                  <Tag color="blue">{item.tone}</Tag>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {item.user_name ? `录入人：${item.user_name} · ` : ''}{fmtCN(item.created_at)}
                  </Text>
                </Space>
              }
              description={
                <div>
                  <div style={{ background: '#fafafa', padding: 8, borderRadius: 4, marginBottom: 4, fontSize: 13 }}>
                    <Text type="secondary">原文：</Text>{item.source_text}
                  </div>
                  <div style={{ background: '#f6ffed', padding: 8, borderRadius: 4, fontSize: 13 }}>
                    <Text type="secondary">译文：</Text>{item.translated_text}
                  </div>
                </div>
              }
            />
          </List.Item>
        )}
      />
    </Card>
  );

  return (
    <div>
      <div className="page-header">
        <h2>专业翻译</h2>
        <p className="desc">基于物流行业术语库的专业翻译，支持中性/强硬/生气/不耐烦多种语气，拟人化表达</p>
      </div>

      <Card className="card-shadow" styles={{ body: { padding: 0 } }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: 'translate', label: <span><TranslationOutlined /> 翻译</span>, children: <div style={{ padding: 16 }}>{translateTab}</div> },
            { key: 'terms', label: <span><BookOutlined /> 术语库 ({terms.length})</span>, children: <div style={{ padding: 16 }}>{termsTab}</div> },
            { key: 'history', label: <span><HistoryOutlined /> 翻译历史</span>, children: <div style={{ padding: 16 }}>{historyTab}</div> },
          ]}
        />
      </Card>
    </div>
  );
}
